import Echart from "echarts-for-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { langStore } from "../../../../stores/lang-store"

const useContainerWidth = () => {
  const ref = useRef(null)
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!ref.current) return

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? 0
      setWidth(w)
    })

    ro.observe(ref.current)
    return () => ro.disconnect()
  }, [])

  return [ref, width]
}

const MixedLineBarChart = ({ datasets }) => {
  const [wrapRef, width] = useContainerWidth()

  const isMobile = width > 0 && width < 640

  const { lang } = langStore()

  const option = useMemo(() => {
    const xLabels = datasets.xLabels
    const barSeries = datasets.ySeries[0]
    const lineSeries = datasets.ySeries[1]

    const labelCount = xLabels.length

    const showOnlyFewLabels = (value, index) => {
      if (!isMobile) return value?.slice(5) || value // 데스크탑: MM-DD
      if (labelCount <= 3) return value?.slice(5) || value

      const mid = Math.floor((labelCount - 1) / 2)
      const pick = index === 0 || index === mid || index === labelCount - 1
      return pick ? value?.slice(5) || value : ""
    }

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        backgroundColor: "rgba(255,255,255,0.95)",
        borderColor: "#ddd",
        borderRadius: 10,
        textStyle: { color: "#333", fontSize: isMobile ? 12 : 13 },

        // TODO: 임시로 먼저 만들어놓음. 더 좋은 툴팁 디자인을 찾아낼 것.
        formatter: (params) => {
          const date = params[0]?.axisValue
          const volume = params.find((p) => p.seriesName === barSeries.name)
          const households = params.find((p) => p.seriesName === lineSeries.name)

          if (lang === "ko") {
            return `
              <div style="font-weight:600;margin-bottom:6px">${date}</div>
              ${volume ? `누수량: <b>${volume.value}</b> m³<br/>` : ""}
              ${households ? `누수 수용가 수: <b>${households.value}</b> 가구` : ""}
            `
          }

          return `
            <div style="font-weight:600;margin-bottom:6px">${date}</div>
            ${volume ? `Daily Leakage Volume <br/> <b>${volume.value}</b> m³<br>` : ""}
            ${households ? `Leaking Households <br/> <b>${households.value}</b>` : ""}
          `
        }
      },

      legend: {
        top: 10,
        left: "center",
        itemWidth: isMobile ? 10 : 12,
        itemHeight: isMobile ? 10 : 12,
        textStyle: { color: "#444", fontWeight: 500, fontSize: isMobile ? 11 : 13 }
      },

      grid: isMobile
        ? { left: 40, right: 40, top: 55, bottom: 35 }
        : { left: 60, right: 60, top: 60, bottom: 30 },

      xAxis: {
        type: "category",
        data: xLabels,
        axisLine: { lineStyle: { color: "#e3e3e3" } },
        axisTick: { lineStyle: { color: "#e3e3e3" } },
        axisLabel: {
          color: "#555",
          fontSize: isMobile ? 10 : 12,
          formatter: showOnlyFewLabels
        }
      },

      yAxis: [
        {
          type: "value",
          name: isMobile ? "" : datasets.leftLabel,
          nameTextStyle: { color: "#666", fontWeight: 500 },
          axisLabel: { color: "#555", fontSize: isMobile ? 10 : 12 },
          splitLine: { lineStyle: { color: "#e8e8e8" } }
        },
        {
          type: "value",
          position: "right",
          name: isMobile ? "" : datasets.rightLabel,
          nameTextStyle: { color: "#666", fontWeight: 500 },
          axisLabel: { color: "#555", fontSize: isMobile ? 10 : 12 },
          splitLine: { show: false }
        }
      ],

      series: [
        {
          name: barSeries.name,
          type: "bar",
          data: barSeries.data,
          barMaxWidth: isMobile ? 14 : 24,
          itemStyle: {
            borderRadius: [6, 6, 0, 0],
            color: barSeries.color
          },
          tooltip: { valueFormatter: (v) => `${v} m³` }
        },
        {
          name: lineSeries.name,
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          symbol: "circle",
          symbolSize: isMobile ? 5 : 8,
          lineStyle: { width: isMobile ? 2 : 3, color: lineSeries.color },
          itemStyle: { color: lineSeries.color },
          data: lineSeries.data,
          tooltip: { valueFormatter: (v) => `${v} ${lineSeries.tooltip.unit}` }
        }
      ]
    }
  }, [datasets, isMobile, lang])

  return (
    <div ref={wrapRef} className="h-full min-h-60">
      <Echart option={option} notMerge style={{ width: "100%", height: "100%" }} />
    </div>
  )
}

export default MixedLineBarChart
